import React from 'react';
import styled from 'styled-components';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../generic/ui/Mono';
import { Tag } from '../../../../../generic/ui/Tag/Tag';
import InvoiceIcon from '../../../../../generic/icons/IconLib/InvoiceIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import WarehouseIcon from '../../../../../generic/icons/IconLib/WarehouseIcon';
import { convertToDate } from '../../../../../modules/convertDateString';
import { ITeudotItem } from '../../../../../../models/RequestsModel/teudot';

interface Props {
    data: ITeudotItem;
    onEdit: () => void;
}

const GROUP_TRIM_COLOR = '#22C55E';
const GROUP_OTHER_COLOR = '#F59E0B';
const WAREHOUSE_COLOR = '#0891B2';

const TeudaTitleCard: React.FC<Props> = ({ data, onEdit }) => {
    const {
        Delivery_Num,
        Order_Num,
        Delivery_Date,
        Customer_Name,
        TranzitDesc,
        GroupDesc,
    } = data;

    const date = Delivery_Date ? convertToDate(Delivery_Date) : '';
    const customer = (Customer_Name ?? '').trim();
    const tranzit = (TranzitDesc ?? '').trim();
    const group = (GroupDesc ?? '').trim();
    const isTrim = group === 'טריים';
    const hasOrder = !!Order_Num && Order_Num !== 0;

    return (
        <Card>
            <CardRow
                $columns='6.5rem minmax(0, 1fr) auto auto'
                $mobileColumns='auto minmax(0, 1fr) auto auto'
                $isLast
                $hover={false}
            >
                <CardRow.Cell>
                    <CardRow.Stack $gap='0.125rem'>
                        <CardRow.Line $gap='0.375rem'>
                            <SmallIcon>
                                <InvoiceIcon color='currentColor' size={16} />
                            </SmallIcon>
                            <Id>{Delivery_Num}</Id>
                        </CardRow.Line>
                        {hasOrder && <Reference>{`#${Order_Num}`}</Reference>}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell>
                    <CardRow.Stack $gap='0.375rem'>
                        <CardRow.Line $gap='0.5rem'>
                            <SmallIcon $muted>
                                <UserIcon color='currentColor' size={16} />
                            </SmallIcon>
                            <PrimaryName>{customer}</PrimaryName>
                        </CardRow.Line>
                        {(group || tranzit) && (
                            <CardRow.Line $gap='0.375rem' $wrap>
                                {group && (
                                    <Tag
                                        variant='tinted'
                                        color={isTrim ? GROUP_TRIM_COLOR : GROUP_OTHER_COLOR}
                                    >
                                        {group}
                                    </Tag>
                                )}
                                {tranzit && (
                                    <Tag variant='tinted' color={WAREHOUSE_COLOR}>
                                        <ChipInner>
                                            <ChipIcon>
                                                <WarehouseIcon />
                                            </ChipIcon>
                                            <ChipText>{tranzit}</ChipText>
                                        </ChipInner>
                                    </Tag>
                                )}
                            </CardRow.Line>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    {date && (
                        <CardRow.Stack $gap='0.125rem' $align='center'>
                            <When>{date}</When>
                        </CardRow.Stack>
                    )}
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    <EditBtn type='button' onClick={onEdit} aria-label='edit teuda title'>
                        <PencilIcon color='currentColor' size={16} />
                    </EditBtn>
                </CardRow.Cell>
            </CardRow>
        </Card>
    );
};

export default TeudaTitleCard;

const Card = styled.div`
    position: relative;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;
`;

const SmallIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const Reference = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.ink4};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const PrimaryName = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const EditBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.colors.line};
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink3};
    cursor: pointer;
    border-radius: 0.5rem;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accent};
        color: ${({ theme }) => theme.colors.ink};
        border-color: ${({ theme }) => theme.colors.accent};
    }

    &:active {
        transform: translateY(1px);
    }
`;

const ChipInner = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: inherit;
`;

const ChipIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: inherit;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

const ChipText = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.125rem;
`;
